import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { ApiError } from '@/lib/api/client';
import { profileApi } from '@/lib/api/endpoints';
import type { NotificationEventPref, NotificationSettings } from '@/lib/api/types';
import { DRAWER_ROLE_SWITCH } from '@/lib/navigation';
import { routes } from '@/lib/routes';
import { getRouteForRoleOrCategory } from '@/lib/roleRoutes';

const RETURN_KEY = 'za.notifications.returnTo';

import {
  type InboxItem,
  KIND_ICON,
  KIND_COLOR,
  getStoredInbox,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  dismissNotification,
} from '@/lib/notificationsStorage';

export default function NotificationsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const userId = user?.id;
  const { notify } = useToast();
  const activeRole = user?.activeRole || 'SYSTEM_OWNER';
  const roleLabel =
    DRAWER_ROLE_SWITCH.find((item) => item.code === activeRole)?.label || activeRole;

  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<'inbox' | 'settings'>('inbox');
  const [inbox, setInbox] = useState<InboxItem[]>(() => getStoredInbox(userId));

  useEffect(() => {
    setInbox(getStoredInbox(userId));
  }, [userId]);

  useEffect(() => {
    const state = location.state as { returnTo?: string } | null;
    if (state?.returnTo) {
      sessionStorage.setItem(RETURN_KEY, state.returnTo);
    } else if (!sessionStorage.getItem(RETURN_KEY)) {
      sessionStorage.setItem(RETURN_KEY, getRouteForRoleOrCategory(activeRole) || routes.app);
    }
  }, [location.state, activeRole]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    profileApi
      .getNotifications(activeRole)
      .then((payload) => {
        if (!cancelled) setSettings(payload);
      })
      .catch((error) => {
        const message =
          error instanceof ApiError ? error.message : 'Не удалось загрузить уведомления';
        notify(message, 'error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeRole, notify]);

  const unreadCount = inbox.filter((n) => !n.read).length;
  const markAllRead = () => {
    const next = markAllNotificationsAsRead(userId);
    setInbox(next);
  };
  const markRead = (id: string) => {
    const next = markNotificationAsRead(id, userId);
    setInbox(next);
  };
  const dismiss = (id: string) => {
    const next = dismissNotification(id, userId);
    setInbox(next);
  };

  const persist = async (patch: Parameters<typeof profileApi.patchNotifications>[0]) => {
    setSaving(true);
    try {
      const next = await profileApi.patchNotifications(patch, activeRole);
      setSettings(next);
      notify('Настройки уведомлений сохранены', 'success');
      return true;
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Ошибка сохранения';
      notify(message, 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const updateEvent = (id: string, key: 'platform' | 'email', value: boolean) => {
    if (!settings) return;
    setSettings({
      ...settings,
      events: settings.events.map((ev) => {
        if (ev.id !== id) return ev;
        if (ev.mandatory && key === 'platform') return { ...ev, platform: true };
        return { ...ev, [key]: value };
      }),
    });
  };

  const handleBack = () => {
    const returnTo = sessionStorage.getItem(RETURN_KEY) || routes.app;
    sessionStorage.removeItem(RETURN_KEY);
    navigate(returnTo);
  };

  return (
    <div className="notifications-page">
      <div className="notifications-shell">

        <header className="notifications-top">
          <button type="button" className="profile-btn" onClick={handleBack}>
            ← Назад
          </button>
          <div className="notifications-crumb">
            <h1>Уведомления</h1>
          </div>
          <div className="notifications-top-actions">
            {tab === 'settings' && settings && (
              <button
                type="button"
                className="profile-btn primary"
                disabled={saving}
                onClick={() =>
                  void persist({
                    email_notifications: settings.email_notifications,
                    notification_mode: settings.notification_mode,
                    do_not_disturb: settings.do_not_disturb,
                    timezone: settings.timezone,
                    events: settings.events,
                  })
                }
              >
                Сохранить
              </button>
            )}
            <button
              type="button"
              className="profile-btn"
              onClick={() => navigate(routes.profileSection('settings'))}
            >
              Профиль
            </button>
            <button type="button" className="profile-btn" onClick={handleBack}>
              Закрыть
            </button>
          </div>
        </header>

        <div className="notif-tabs">
          <button
            type="button"
            className={`notif-tab${tab === 'inbox' ? ' active' : ''}`}
            onClick={() => setTab('inbox')}
          >
            Входящие
            {unreadCount > 0 && <span className="notif-badge">{unreadCount}</span>}
          </button>
          <button
            type="button"
            className={`notif-tab${tab === 'settings' ? ' active' : ''}`}
            onClick={() => setTab('settings')}
          >
            Настройки
          </button>
        </div>

        {tab === 'inbox' && (
          <section className="notif-inbox">
            {unreadCount > 0 && (
              <div className="notif-inbox-actions">
                <button type="button" className="notif-mark-all" onClick={markAllRead}>
                  Отметить все как прочитанные
                </button>
              </div>
            )}
            {inbox.length === 0 ? (
              <div className="notif-empty">
                <span className="notif-empty-icon">🔔</span>
                <p>Уведомлений нет</p>
              </div>
            ) : (
              <ul className="notif-list">
                {inbox.map((item) => (
                  <li
                    key={item.id}
                    className={`notif-item${item.read ? '' : ' unread'}`}
                    onClick={() => markRead(item.id)}
                  >
                    <span className="notif-item-icon" style={{ color: KIND_COLOR[item.kind] }}>
                      {KIND_ICON[item.kind]}
                    </span>
                    <div className="notif-item-body">
                      <div className="notif-item-top">
                        <strong>{item.title}</strong>
                        <span className="notif-item-ts">{item.ts}</span>
                      </div>
                      <p className="notif-item-text">{item.body}</p>
                      {item.link && (
                        <button
                          type="button"
                          className="notif-item-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(item.link!);
                          }}
                        >
                          Перейти →
                        </button>
                      )}
                    </div>
                    {!item.read && <span className="notif-dot" />}
                    <button
                      type="button"
                      className="notif-dismiss"
                      title="Удалить"
                      onClick={(e) => {
                        e.stopPropagation();
                        dismiss(item.id);
                      }}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {tab === 'settings' && (
          <>
            {loading || !settings ? (
              <p className="notifications-loading">Загрузка…</p>
            ) : (
              <>
                <div className="notifications-grid">
                  <article className="notifications-panel">
                    <header>
                      <div>
                        <h3>Каналы и режим</h3>
                        <p>Служебные уведомления включены по умолчанию.</p>
                      </div>
                    </header>
                    <label className="profile-check">
                      <input
                        type="checkbox"
                        checked={settings.email_notifications}
                        onChange={(e) =>
                          setSettings({ ...settings, email_notifications: e.target.checked })
                        }
                      />
                      <span>Дублировать важные уведомления на email</span>
                    </label>
                    <div className="notifications-fields">
                      <label className="profile-field">
                        <span>Режим доставки</span>
                        <select
                          value={settings.notification_mode === 'digest' ? 'digest' : 'instant'}
                          onChange={(e) =>
                            setSettings({ ...settings, notification_mode: e.target.value })
                          }
                        >
                          <option value="instant">Мгновенные уведомления</option>
                          <option value="digest">Ежедневная сводка</option>
                        </select>
                      </label>
                      <label className="profile-field">
                        <span>Не беспокоить</span>
                        <input
                          value={settings.do_not_disturb}
                          onChange={(e) =>
                            setSettings({ ...settings, do_not_disturb: e.target.value })
                          }
                        />
                      </label>
                      <label className="profile-field">
                        <span>Часовой пояс</span>
                        <input
                          value={settings.timezone}
                          onChange={(e) =>
                            setSettings({ ...settings, timezone: e.target.value })
                          }
                        />
                      </label>
                    </div>
                    <div className="notifications-actions">
                      <button type="button" onClick={() => void persist({ action: 'enable_all' })}>
                        Включить всё
                      </button>
                      <button
                        type="button"
                        onClick={() => void persist({ action: 'disable_optional' })}
                      >
                        Отключить необязательные
                      </button>
                      <button
                        type="button"
                        className="primary"
                        disabled={saving}
                        onClick={() =>
                          void persist({
                            email_notifications: settings.email_notifications,
                            notification_mode: settings.notification_mode,
                            do_not_disturb: settings.do_not_disturb,
                            timezone: settings.timezone,
                            events: settings.events,
                          })
                        }
                      >
                        Сохранить
                      </button>
                    </div>
                  </article>

                  <article className="notifications-panel">
                    <header>
                      <div>
                        <h3>Пример email-уведомления</h3>
                        <p>Письмо не содержит закрытых данных.</p>
                      </div>
                    </header>
                    {(() => {
                      const preview = settings.safe_email_preview || {};
                      return (
                        <div className="notifications-safe-card">
                          <small>От: {preview.from || 'noreply@zertteu.academy'}</small>
                          <h4>{preview.event || 'Служебное уведомление'}</h4>
                          <p>
                            Тема: {preview.subject || 'Важное событие в Zertteu Academy'}
                            <br />
                            Дата: {preview.when || 'сегодня'}
                          </p>
                          <p className="notifications-safe-body">
                            {preview.body ||
                              'Краткое описание события без закрытых данных проектов.'}
                          </p>
                          <button type="button" className="profile-btn primary">
                            {preview.cta || 'Открыть в Zertteu Academy'}
                          </button>
                        </div>
                      );
                    })()}
                  </article>
                </div>

                <section className="notifications-panel">
                  <header>
                    <div>
                      <h3>Типы уведомлений</h3>
                      <p>Роль: {roleLabel}</p>
                    </div>
                  </header>
                  <div className="notifications-types">
                    {settings.events.map((ev: NotificationEventPref) => (
                      <article key={ev.id}>
                        <h4>{ev.label}</h4>
                        <label className="profile-check">
                          <input
                            type="checkbox"
                            checked={ev.platform}
                            disabled={ev.mandatory}
                            onChange={(e) => updateEvent(ev.id, 'platform', e.target.checked)}
                          />
                          <span>В платформе</span>
                        </label>
                        <label className="profile-check">
                          <input
                            type="checkbox"
                            checked={ev.email}
                            onChange={(e) => updateEvent(ev.id, 'email', e.target.checked)}
                          />
                          <span>Email</span>
                        </label>
                        {ev.mandatory && (
                          <span className="notif-mandatory-tag">обязательное</span>
                        )}
                      </article>
                    ))}
                  </div>
                </section>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
